import React, { useEffect } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import ContactForm from '../../components/ContactForm'
import { track } from '../../helpers/track'

// Guides SEO/GEO : les requêtes que tapent les dentistes (« devenir dentiste
// en France », « inscription Ordre », « salaire dentiste ») sont aujourd'hui
// captées par des cabinets de recrutement généralistes. Notre avantage : des
// chiffres réels. Pages pré-rendues (react-snap) → lisibles par Google ET les
// crawlers IA sans JavaScript.

const CTA_CALC = { fr: 'Calculer mon salaire net', en: 'Calculate my net salary' }

const GUIDES = {
    'devenir-dentiste-en-france': {
        title: { fr: 'Devenir dentiste en France avec un diplôme européen — le guide 2026', en: 'Becoming a dentist in France with an EU degree — the 2026 guide' },
        intro: {
            fr: "Vous êtes chirurgien-dentiste diplômé dans l'Union européenne et la France vous attire ? Bonne nouvelle : grâce à la reconnaissance automatique des diplômes (directive 2005/36/CE), le parcours est balisé — et bien accompagné, il prend quelques mois, pas des années. Voici les étapes réelles, vécues par les praticiens que nous avons installés.",
            en: "You are a dentist with an EU degree and France appeals to you? Good news: thanks to automatic recognition of diplomas (directive 2005/36/EC), the path is well marked — and with proper support it takes months, not years. Here are the real steps, as experienced by the practitioners we have relocated.",
        },
        sections: [
            {
                h2: { fr: 'Qui peut exercer en France ?', en: 'Who can practice in France?' },
                p: {
                    fr: "Deux conditions : la citoyenneté d'un État membre de l'UE (ou EEE/Suisse) et un diplôme de chirurgien-dentiste délivré par une université de l'UE, accompagné d'une attestation de conformité de l'État d'origine. Dans ce cadre, la reconnaissance est automatique : pas d'équivalence à repasser, pas d'examen français. Les diplômés hors UE suivent un parcours différent (EVC), beaucoup plus long.",
                    en: "Two conditions: citizenship of an EU member state (or EEA/Switzerland) and a dental degree delivered by an EU university, with a certificate of conformity from the issuing state. In that framework, recognition is automatic: no equivalence exam, no French diploma to retake. Non-EU graduates follow a different, much longer path.",
                },
            },
            {
                h2: { fr: 'Le français : le vrai sujet, et comment on le règle', en: 'French: the real topic, and how we solve it' },
                p: {
                    fr: "L'Ordre vérifie que vous pouvez exercer en toute sécurité en français, notamment lors d'un entretien. La méthode qui marche chez nous : des cours hebdomadaires pendant environ 3 mois avant l'arrivée, puis un bootcamp intensif de 20 heures avec une professeure de français juste avant l'entretien à l'Ordre, préparation ciblée sur le vocabulaire clinique et les questions types. Nos praticiens portugais, roumains et grecs sont tous passés par là.",
                    en: "The Ordre checks that you can practice safely in French, notably through an interview. What works for us: weekly lessons for about 3 months before the move, then an intensive 20-hour bootcamp with a French teacher right before the Ordre interview, targeting clinical vocabulary and typical questions. Our Portuguese, Romanian and Greek practitioners all went through it.",
                },
            },
            {
                h2: { fr: 'Les 5 étapes, avec les vrais délais', en: 'The 5 steps, with real timelines' },
                list: {
                    fr: [
                        "1. Préparation (1 à 6 mois) — définition du poste, du contrat, et démarrage du français si besoin.",
                        "2. Inscription à l'Ordre (~2 mois) — nous rassemblons les documents et gérons le dossier avec vous (voir notre guide dédié).",
                        "3. Installation (~1 semaine) — logement offert les 3 premiers mois, aide banque / fiscalité / école des enfants.",
                        "4. Intégration (~1 semaine) — visite des cabinets, rencontre de l'équipe, spécificités du système de soins français (sécurité sociale, ordonnances).",
                        "5. Démarrage — planning rempli dès le premier jour, assistante dédiée, zéro administratif.",
                    ],
                    en: [
                        "1. Preparation (1 to 6 months) — role and contract definition, French lessons if needed.",
                        "2. Ordre registration (~2 months) — we gather the documents and manage the file with you (see our dedicated guide).",
                        "3. Relocation (~1 week) — housing provided for the first 3 months, help with bank / taxes / schools.",
                        "4. Integration (~1 week) — clinic tour, meeting the team, specifics of the French healthcare system.",
                        "5. Start — full schedule from day one, dedicated assistant, zero admin work.",
                    ],
                },
            },
            {
                h2: { fr: 'Salarié ou libéral ?', en: 'Employed or self-employed?' },
                p: {
                    fr: "En arrivant de l'étranger, le salariat est le chemin le plus sûr : salaire minimum garanti, zéro gestion administrative, assistante dédiée, et la patientèle est déjà là. Chez Neomed, nos dentistes salariés gagnent 6 000 à 13 000 € nets par mois (30 % et plus des revenus générés) — consultez notre guide des salaires pour le détail.",
                    en: "Arriving from abroad, salaried practice is the safest path: guaranteed minimum salary, zero admin, dedicated assistant, and the patients are already there. At Neomed, our salaried dentists earn €6,000 to €13,000 net per month (30%+ of generated revenue) — see our salary guide for details.",
                },
            },
            {
                h2: { fr: 'Où s\'installer ? Le paradoxe français', en: 'Where to settle? The French paradox' },
                p: {
                    fr: "La France manque de dentistes — mais pas partout. Dans les grandes métropoles, la concurrence est rude ; dans des départements comme l'Indre (à 2 h de Paris), la demande de soins est immense et un praticien démarre avec un planning plein immédiatement. C'est exactement là que sont nos trois cabinets (Châteauroux, Déols, Saint-Maur) : 11 salles de soins, équipements récents, 4,8/5 sur Google.",
                    en: "France lacks dentists — but not everywhere. In big cities competition is fierce; in departments like the Indre (2h from Paris) demand is huge and a practitioner starts with a full schedule immediately. That is exactly where our three clinics are (Châteauroux, Déols, Saint-Maur): 11 treatment rooms, modern equipment, 4.8/5 on Google.",
                },
            },
        ],
        related: ['inscription-ordre-chirurgiens-dentistes', 'salaire-dentiste-france'],
    },

    'inscription-ordre-chirurgiens-dentistes': {
        title: { fr: "S'inscrire à l'Ordre des chirurgiens-dentistes avec un diplôme UE : documents et délais", en: 'Registering with the French dental council (Ordre) with an EU degree: documents and timelines' },
        intro: {
            fr: "Impossible d'exercer en France sans être inscrit au tableau de l'Ordre national des chirurgiens-dentistes. Pour un diplômé de l'UE, c'est une procédure administrative — exigeante sur les pièces, mais sans examen. Voici comment elle se déroule réellement, et comment nous la prenons en charge.",
            en: "You cannot practice in France without being registered with the national dental council (Ordre). For an EU graduate it is an administrative procedure — demanding on paperwork, but with no exam. Here is how it actually unfolds, and how we handle it.",
        },
        sections: [
            {
                h2: { fr: 'Le principe', en: 'The principle' },
                p: {
                    fr: "L'inscription se fait auprès du conseil départemental de l'Ordre du lieu d'exercice (pour nos cabinets : l'Indre). Le conseil vérifie vos diplômes, votre honorabilité et votre maîtrise du français, généralement lors d'un entretien. Une fois inscrit, vous recevez votre numéro et pouvez exercer partout en France.",
                    en: "Registration happens with the departmental council of the Ordre where you will practice (for our clinics: the Indre). The council checks your diplomas, your good standing and your command of French, usually through an interview. Once registered, you receive your number and can practice anywhere in France.",
                },
            },
            {
                h2: { fr: 'Les documents typiquement demandés', en: 'Documents typically required' },
                list: {
                    fr: [
                        "Diplôme de chirurgien-dentiste + attestation de conformité UE délivrée par l'État d'origine",
                        "Pièce d'identité prouvant la citoyenneté UE/EEE",
                        "Extrait de casier judiciaire (ou équivalent) et attestation de non-sanction de l'ordre d'origine (« certificat de moralité »)",
                        "Traductions assermentées des documents non francophones",
                        "Formulaire d'inscription et curriculum vitae",
                    ],
                    en: [
                        "Dental degree + EU certificate of conformity from the issuing state",
                        "ID proving EU/EEA citizenship",
                        "Criminal record extract (or equivalent) and certificate of good standing from your home council",
                        "Sworn translations of non-French documents",
                        "Registration form and curriculum vitae",
                    ],
                },
                p: {
                    fr: "La liste exacte varie d'un conseil départemental à l'autre — chacun a ses habitudes. C'est justement ce qu'on prend en charge : le dossier se constitue avec vous, pièce par pièce.",
                    en: "The exact list varies from one departmental council to another — each has its habits. That is exactly what we take off your plate: the file gets built with you, document by document.",
                },
            },
            {
                h2: { fr: "L'entretien et le délai réel", en: 'The interview and the real timeline' },
                p: {
                    fr: "Comptez environ 2 mois entre le dépôt du dossier complet et l'inscription, entretien compris. L'entretien porte sur votre parcours et vérifie votre français professionnel : on le prépare de façon ciblée, avec le bootcamp de 20 heures juste avant la date. Résultat : nos praticiens arrivent préparés, et nettement moins stressés qu'ils ne l'imaginaient.",
                    en: "Count roughly 2 months between filing the complete file and registration, interview included. The interview covers your background and checks your professional French: we prepare it specifically, with the 20-hour bootcamp right before the date. Our practitioners arrive prepared — and far less stressed than they expected.",
                },
            },
            {
                h2: { fr: 'Et après ?', en: 'And after?' },
                p: {
                    fr: "Une fois inscrit : enregistrement auprès de l'Assurance Maladie du département, assurance responsabilité civile professionnelle, et c'est parti. En tant que praticien salarié Neomed, tout cela est organisé pour vous — vous soignez, on s'occupe du reste.",
                    en: "Once registered: enrolment with the local health insurance office, professional liability insurance, and you are set. As a salaried Neomed practitioner, all of this is organised for you — you treat patients, we handle the rest.",
                },
            },
        ],
        related: ['devenir-dentiste-en-france', 'salaire-dentiste-france'],
    },

    'salaire-dentiste-france': {
        title: { fr: 'Salaire d\'un dentiste en France en 2026 : les vrais chiffres', en: 'Dentist salary in France in 2026: the real numbers' },
        intro: {
            fr: "Combien gagne vraiment un dentiste en France ? Les moyennes nationales mélangent tout : libéraux surchargés de frais, salariés de centres low-cost, jeunes diplômés et praticiens installés. Voici des chiffres concrets — dont ceux, réels, de nos propres cabinets.",
            en: "How much does a dentist really earn in France? National averages mix everything: self-employed with heavy costs, low-cost clinic employees, new graduates and established practitioners. Here are concrete numbers — including the real ones from our own clinics.",
        },
        sections: [
            {
                h2: { fr: 'Libéral vs salarié : comparer ce qui est comparable', en: 'Self-employed vs salaried: compare like for like' },
                p: {
                    fr: "En libéral, le chiffre d'affaires peut être élevé mais il faut en soustraire le loyer, l'équipement, l'assistante, l'URSSAF, la gestion… et les trous de planning. En salariat, le pourcentage paraît plus faible mais il est net de tout : zéro charge de structure, zéro administratif, salaire garanti même le premier mois. La vraie comparaison se fait sur le net en poche par heure travaillée.",
                    en: "Self-employed turnover can be high, but you must subtract rent, equipment, assistant, social charges, management… and schedule gaps. Salaried percentages look lower but are net of everything: zero structural costs, zero admin, guaranteed salary even in month one. The real comparison is net take-home per hour worked.",
                },
            },
            {
                h2: { fr: 'Les chiffres réels de nos cabinets', en: 'The real numbers from our clinics' },
                list: {
                    fr: [
                        "Rémunération : 30 % et plus des revenus générés, minimum garanti de 3 000 € brut/mois",
                        "Dentistes omnipraticiens : 6 000 à 13 000 € NETS/mois constatés",
                        "Orthodontistes : 8 000 à 15 000 € NETS/mois constatés",
                        "Semaine de 4 jours (9 h – 18 h), 6 semaines de congés payés",
                        "Planning rempli dès le premier jour — la patientèle attend, pas vous",
                    ],
                    en: [
                        "Pay: 30%+ of generated revenue, guaranteed minimum of €3,000 gross/month",
                        "General dentists: €6,000 to €13,000 NET/month observed",
                        "Orthodontists: €8,000 to €15,000 NET/month observed",
                        "4-day week (9am – 6pm), 6 weeks of paid holidays",
                        "Full schedule from day one — the patients wait for you, not the reverse",
                    ],
                },
            },
            {
                h2: { fr: 'Ce qui fait varier le salaire', en: 'What drives the salary' },
                p: {
                    fr: "Trois facteurs dominent : le volume de patientèle (énorme dans les zones sous-dotées comme l'Indre, faible dans les métropoles saturées), l'organisation du cabinet (une assistante dédiée au fauteuil augmente directement le nombre d'actes), et votre spécialisation (implantologie et orthodontie tirent les revenus vers le haut — nous formons d'ailleurs nos praticiens à l'implantologie).",
                    en: "Three factors dominate: patient volume (huge in under-served areas like the Indre, low in saturated cities), practice organisation (a dedicated chairside assistant directly increases output), and your specialisation (implantology and orthodontics push revenue up — we actually train our practitioners in implantology).",
                },
            },
            {
                h2: { fr: 'Et les impôts ?', en: 'What about taxes?' },
                p: {
                    fr: "Le net après impôt dépend de votre situation familiale (parts fiscales) et d'éventuels avantages pour les praticiens venus de l'étranger. Notre calculateur en ligne fait la simulation complète — brut, net, net après impôt — selon votre structure familiale, en une minute.",
                    en: "Net after tax depends on your family situation (tax shares) and possible benefits for practitioners moving from abroad. Our online calculator runs the full simulation — gross, net, net after tax — for your family structure, in one minute.",
                },
            },
        ],
        related: ['devenir-dentiste-en-france', 'inscription-ordre-chirurgiens-dentistes'],
    },
}


// Pages internationales : UNE page complète par langue de recrutement, avec
// sa propre URL indexable — le toggle FR/EN du site ne crée pas d'URL par
// langue, ces pages ciblent les recherches en roumain, portugais, espagnol
// et grec. Contenu autoporteur (offre + salaires + étapes + compatriotes).
const INTL_GUIDES = {
    'dentist-in-franta': {
        lang: 'ro',
        title: 'Dentist în Franța cu diplomă din UE — salarii reale și pașii concreți (2026)',
        intro: "Ești medic stomatolog cu diplomă obținută în Uniunea Europeană? În Franța, diploma ta este recunoscută automat (directiva 2005/36/CE) — fără examene de echivalare. La cabinetele Neomed din centrul Franței (la 2 ore de Paris), dentiștii salariați câștigă între 6.000 și 13.000 € NET pe lună, cu program plin din prima zi.",
        sections: [
            { h2: 'Cât câștigă cu adevărat un dentist la Neomed', list: [
                "30%+ din veniturile generate, cu un minim garantat de 3.000 € brut/lună",
                "Dentiști: 6.000 – 13.000 € NET/lună · Ortodonți: 8.000 – 15.000 € NET/lună",
                "Săptămână de 4 zile (9–18), 6 săptămâni de concediu plătit",
                "Asistentă dedicată, zero birocrație, zero clauze restrictive în contract",
            ]},
            { h2: 'Pașii, cu termene reale', list: [
                "1. Pregătire (1–6 luni): definirea postului și cursuri de franceză săptămânale",
                "2. Înscrierea la Ordinul medicilor stomatologi (~2 luni) — ne ocupăm de dosar împreună cu tine",
                "3. Bootcamp intensiv de franceză de 20 de ore chiar înainte de interviul la Ordin, cu pregătire țintită",
                "4. Instalare: locuință oferită în primele 3 luni, ajutor cu banca, taxele, școala copiilor",
                "5. Start: program plin din prima zi",
            ]},
            { h2: 'Colegii tăi români sunt deja aici', p: "Dr Radu Zaharii (endodonție, format ulterior în implantologie la noi) s-a alăturat echipei în decembrie 2025 — iar soția lui, Dr Nausica Zaharii, l-a urmat ca medic stomatolog: dovada că modelul funcționează. Vorbim românește la telefon dacă preferi." },
        ],
        cta: 'Aplică în 2 minute — răspuns în 24 h',
    },
    'dentista-em-franca': {
        lang: 'pt',
        title: 'Dentista em França com diploma da UE — salários reais e passos concretos (2026)',
        intro: "É médico dentista com diploma obtido na União Europeia? Em França o seu diploma é reconhecido automaticamente (diretiva 2005/36/CE) — sem exames de equivalência. Nas clínicas Neomed no centro de França (a 2 horas de Paris), os dentistas assalariados ganham entre 6.000 e 13.000 € LÍQUIDOS por mês, com agenda cheia desde o primeiro dia.",
        sections: [
            { h2: 'Quanto ganha realmente um dentista na Neomed', list: [
                "30%+ das receitas geradas, com um mínimo garantido de 3.000 € brutos/mês",
                "Dentistas: 6.000 – 13.000 € líquidos/mês · Ortodontistas: 8.000 – 15.000 € líquidos/mês",
                "Semana de 4 dias (9h–18h), 6 semanas de férias pagas",
                "Assistente dedicada, zero burocracia, zero cláusulas restritivas no contrato",
            ]},
            { h2: 'Os passos, com prazos reais', list: [
                "1. Preparação (1–6 meses): definição do posto e aulas semanais de francês",
                "2. Inscrição na Ordem francesa (~2 meses) — tratamos do processo consigo",
                "3. Bootcamp intensivo de francês de 20 horas mesmo antes da entrevista na Ordem, com preparação dirigida",
                "4. Instalação: alojamento oferecido nos primeiros 3 meses, ajuda com banco, impostos, escola dos filhos",
                "5. Início: agenda cheia desde o primeiro dia",
            ]},
            { h2: 'Os seus colegas portugueses já cá estão', p: "A Dra Susana Coelho (formada na CESPU, no Porto, com mais de 10 anos no Reino Unido) juntou-se à equipa em dezembro de 2025 — encontrou o seu ritmo, trabalha à sua maneira e acabou de comprar casa em Châteauroux. Falamos português ao telefone se preferir." },
        ],
        cta: 'Candidate-se em 2 minutos — resposta em 24 h',
    },
    'dentista-en-francia': {
        lang: 'es',
        title: 'Dentista en Francia con título de la UE — salarios reales y pasos concretos (2026)',
        intro: "¿Eres odontólogo con título obtenido en la Unión Europea? En Francia tu título se reconoce automáticamente (directiva 2005/36/CE) — sin exámenes de homologación. En las clínicas Neomed del centro de Francia (a 2 horas de París), los dentistas asalariados ganan entre 6.000 y 13.000 € NETOS al mes, con agenda llena desde el primer día.",
        sections: [
            { h2: 'Cuánto gana realmente un dentista en Neomed', list: [
                "30%+ de los ingresos generados, con un mínimo garantizado de 3.000 € brutos/mes",
                "Dentistas: 6.000 – 13.000 € netos/mes · Ortodoncistas: 8.000 – 15.000 € netos/mes",
                "Semana de 4 días (9–18 h), 6 semanas de vacaciones pagadas",
                "Asistente dedicada, cero papeleo, cero cláusulas restrictivas en el contrato",
            ]},
            { h2: 'Los pasos, con plazos reales', list: [
                "1. Preparación (1–6 meses): definición del puesto y clases semanales de francés",
                "2. Colegiación en el Ordre francés (~2 meses) — gestionamos el expediente contigo",
                "3. Bootcamp intensivo de francés de 20 horas justo antes de la entrevista, con preparación dirigida",
                "4. Instalación: alojamiento gratuito los 3 primeros meses, ayuda con banco, impuestos, colegio de los niños",
                "5. Inicio: agenda llena desde el primer día",
            ]},
            { h2: 'Tus colegas ya están aquí', p: "La Dra María Hurtado, titulada en la facultad de odontología de las Islas Baleares, ejerce en nuestra clínica de Châteauroux con un enfoque en cirugía y prótesis. Hablamos español por teléfono si lo prefieres." },
        ],
        cta: 'Postula en 2 minutos — respuesta en 24 h',
    },
    'odontiatros-sti-gallia': {
        lang: 'el',
        title: 'Οδοντίατρος στη Γαλλία με πτυχίο ΕΕ — πραγματικοί μισθοί και συγκεκριμένα βήματα (2026)',
        intro: "Είστε οδοντίατρος με πτυχίο από την Ευρωπαϊκή Ένωση; Στη Γαλλία το πτυχίο σας αναγνωρίζεται αυτόματα (οδηγία 2005/36/ΕΚ) — χωρίς εξετάσεις αντιστοιχίας. Στα ιατρεία Neomed στο κέντρο της Γαλλίας (2 ώρες από το Παρίσι), οι μισθωτοί οδοντίατροι κερδίζουν 6.000 έως 13.000 € ΚΑΘΑΡΑ τον μήνα, με γεμάτο πρόγραμμα από την πρώτη μέρα.",
        sections: [
            { h2: 'Πόσα κερδίζει πραγματικά ένας οδοντίατρος στη Neomed', list: [
                "30%+ των εσόδων που παράγετε, με εγγυημένο ελάχιστο 3.000 € μικτά/μήνα",
                "Οδοντίατροι: 6.000 – 13.000 € καθαρά/μήνα · Ορθοδοντικοί: 8.000 – 15.000 € καθαρά/μήνα",
                "Εβδομάδα 4 ημερών (9–18), 6 εβδομάδες άδεια μετ' αποδοχών",
                "Αποκλειστική βοηθός, μηδενική γραφειοκρατία, μηδενικές περιοριστικές ρήτρες",
            ]},
            { h2: 'Τα βήματα, με πραγματικές προθεσμίες', list: [
                "1. Προετοιμασία (1–6 μήνες): καθορισμός θέσης και εβδομαδιαία μαθήματα γαλλικών",
                "2. Εγγραφή στον γαλλικό Οδοντιατρικό Σύλλογο (~2 μήνες) — αναλαμβάνουμε τον φάκελο μαζί σας",
                "3. Εντατικό bootcamp γαλλικών 20 ωρών ακριβώς πριν από τη συνέντευξη, με στοχευμένη προετοιμασία",
                "4. Εγκατάσταση: δωρεάν στέγαση τους 3 πρώτους μήνες, βοήθεια με τράπεζα, φορολογία, σχολείο",
                "5. Έναρξη: γεμάτο πρόγραμμα από την πρώτη μέρα",
            ]},
            { h2: 'Οι Έλληνες συνάδελφοί σας είναι ήδη εδώ', p: "Ο Δρ Δημήτριος Λιανός (Αριστοτέλειο Θεσσαλονίκης, 30 χρόνια εμπειρίας, εξειδίκευση στην εμφυτευματολογία) εντάχθηκε στην ομάδα το 2026 — μετά από δύσκολη εμπειρία σε πολυκλινική της Λυών, βρήκε εδώ οργάνωση που λειτουργεί και αύξησε τον τζίρο του κατά 50%. Μαζί του και η Δρ Βίκυ Νικοπούλου (Πανεπιστήμιο Αθηνών). Μιλάμε αγγλικά στο τηλέφωνο." },
        ],
        cta: 'Κάντε αίτηση σε 2 λεπτά — απάντηση σε 24 ώρες',
    },
}

export const INTL_SLUGS = Object.keys(INTL_GUIDES)

export const GUIDE_SLUGS = Object.keys(GUIDES)

const RELATED_LABEL = { fr: 'À lire ensuite', en: 'Read next' }

export default function Guides(props) {
    const { isEnglish } = props
    const { slug } = useParams()
    const intl = INTL_GUIDES[slug]
    const guide = GUIDES[slug]
    const lang = isEnglish ? 'en' : 'fr'

    useEffect(() => {
        if (guide) track('guide_view', { slug })
        window.scrollTo(0, 0)
    }, [slug, guide])

    if (intl) return <IntlGuide slug={slug} guide={intl} />
    if (!guide) return <Navigate to="/" replace />

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: guide.title.fr,
        description: guide.intro.fr,
        inLanguage: 'fr',
        author: { '@type': 'Organization', name: 'Neomed France', url: 'https://neomed.fr' },
        publisher: { '@type': 'Organization', name: 'Neomed France', url: 'https://neomed.fr' },
        datePublished: '2026-08-16',
        dateModified: '2026-08-16',
        mainEntityOfPage: `https://neomed.fr/guides/${slug}`,
    }

    return (
        <div className='overflow-x-hidden'>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
            <article className='max-w-3xl mx-auto px-6 pt-32 sm:pt-40 pb-10'>
                <p className="text-md md:text-lg font-semibold leading-7 text-brand-600">
                    {isEnglish ? 'GUIDE' : 'GUIDE'}
                </p>
                <h1 className='text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-6'>{guide.title[lang]}</h1>
                <p className='text-lg leading-8 text-gray-600 mb-10'>{guide.intro[lang]}</p>

                {guide.sections.map((s) => (
                    <section key={s.h2.fr} className='mb-8'>
                        <h2 className='text-xl sm:text-2xl font-bold text-gray-900 mb-3'>{s.h2[lang]}</h2>
                        {s.list && (
                            <ul className='list-disc pl-5 space-y-2 text-gray-600 leading-7 mb-3'>
                                {s.list[lang].map((item) => <li key={item}>{item}</li>)}
                            </ul>
                        )}
                        {s.p && <p className='text-gray-600 leading-7'>{s.p[lang]}</p>}
                    </section>
                ))}

                {/* CTA */}
                <div className='mt-10 rounded-2xl bg-gray-900 p-6 sm:p-8 text-center'>
                    <p className='text-white text-lg sm:text-xl font-bold mb-1'>
                        {isEnglish ? 'Curious what YOU would earn?' : 'Curieux de ce que VOUS gagneriez ?'}
                    </p>
                    <p className='text-gray-300 text-sm mb-5'>
                        {isEnglish ? 'Salary calculator, open positions and application in 2 minutes.' : 'Calculateur de salaire, postes ouverts et candidature en 2 minutes.'}
                    </p>
                    <Link
                        to='/careers'
                        onClick={() => track('guide_cta_click', { slug })}
                        className='inline-block rounded-xl bg-white px-6 py-3 text-sm font-semibold text-gray-900 shadow-lg hover:scale-[1.03] transition-all duration-200'
                    >
                        {CTA_CALC[lang]} &rarr;
                    </Link>
                </div>

                {/* Versions par langue de recrutement */}
                {slug === 'devenir-dentiste-en-france' && (
                    <p className='mt-8 text-sm text-gray-500'>
                        {isEnglish ? 'Also available in: ' : 'Aussi disponible en : '}
                        <Link to='/guides/dentist-in-franta' className='text-brand-600 hover:underline'>🇷🇴 Română</Link>{' · '}
                        <Link to='/guides/dentista-em-franca' className='text-brand-600 hover:underline'>🇵🇹 Português</Link>{' · '}
                        <Link to='/guides/dentista-en-francia' className='text-brand-600 hover:underline'>🇪🇸 Español</Link>{' · '}
                        <Link to='/guides/odontiatros-sti-gallia' className='text-brand-600 hover:underline'>🇬🇷 Ελληνικά</Link>
                    </p>
                )}

                {/* Maillage interne */}
                <div className='mt-10 border-t border-gray-100 pt-6'>
                    <p className='text-sm font-semibold text-gray-900 mb-2'>{RELATED_LABEL[lang]}</p>
                    <ul className='space-y-1'>
                        {guide.related.map((r) => (
                            <li key={r}>
                                <Link to={`/guides/${r}`} className='text-brand-600 hover:underline text-sm'>
                                    {GUIDES[r].title[lang]} &rarr;
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </article>

            <ContactForm isEnglish={isEnglish} />
        </div>
    )
}


// Page langue unique (RO/PT/ES/EL) — autoporteuse, CTA vers /contact.
function IntlGuide({ slug, guide }) {
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: guide.title,
        description: guide.intro,
        inLanguage: guide.lang,
        author: { '@type': 'Organization', name: 'Neomed France', url: 'https://neomed.fr' },
        publisher: { '@type': 'Organization', name: 'Neomed France', url: 'https://neomed.fr' },
        datePublished: '2026-08-16',
        dateModified: '2026-08-16',
        mainEntityOfPage: `https://neomed.fr/guides/${slug}`,
    }
    return (
        <div className='overflow-x-hidden'>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
            <article lang={guide.lang} className='max-w-3xl mx-auto px-6 pt-32 sm:pt-40 pb-16'>
                <p className="text-md md:text-lg font-semibold leading-7 text-brand-600">NEOMED · FRANCE</p>
                <h1 className='text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-6'>{guide.title}</h1>
                <p className='text-lg leading-8 text-gray-600 mb-10'>{guide.intro}</p>
                {guide.sections.map((s) => (
                    <section key={s.h2} className='mb-8'>
                        <h2 className='text-xl sm:text-2xl font-bold text-gray-900 mb-3'>{s.h2}</h2>
                        {s.list && (
                            <ul className='list-disc pl-5 space-y-2 text-gray-600 leading-7 mb-3'>
                                {s.list.map((item) => <li key={item}>{item}</li>)}
                            </ul>
                        )}
                        {s.p && <p className='text-gray-600 leading-7'>{s.p}</p>}
                    </section>
                ))}
                <div className='mt-10 rounded-2xl bg-gray-900 p-6 sm:p-8 text-center'>
                    <Link
                        to='/contact'
                        onClick={() => track('guide_cta_click', { slug })}
                        className='inline-block rounded-xl bg-white px-6 py-3 text-sm font-semibold text-gray-900 shadow-lg hover:scale-[1.03] transition-all duration-200'
                    >
                        {guide.cta} &rarr;
                    </Link>
                    <p className='text-gray-400 text-xs mt-4'>
                        WhatsApp : <a href="https://wa.me/33652547516" className='underline' onClick={() => track('whatsapp_click', { location: 'intl-guide' })}>+33 6 52 54 75 16</a> · hadrien@neomed.fr
                    </p>
                </div>
            </article>
        </div>
    )
}
